import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import '@/App.css';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { RegionProvider } from '@/context/RegionContext';
import { AppDataProvider } from '@/context/AppDataContext';

// Family Office Platform Pages
import HomePage from '@/pages/fo/HomePage';
import LoginPage from '@/pages/fo/LoginPage';
import FOLayout from '@/pages/fo/FOLayout';
import DashboardPage from '@/pages/fo/DashboardPage';
import PortfolioManagementPage from '@/pages/fo/PortfolioManagementPage';
import PublicMarketsDashboard from '@/pages/fo/PublicMarketsDashboard';
import HoldingsPage from '@/pages/fo/HoldingsPage';
import TransactionsPage from '@/pages/fo/TransactionsPage';
import CapitalGainsPage from '@/pages/fo/CapitalGainsPage';
import DataImportPage from '@/pages/fo/DataImportPage';
import DealsPage from '@/pages/fo/DealsPage';
import DealDetailPage from '@/pages/fo/DealDetailPage';
import ApprovalsPage from '@/pages/fo/ApprovalsPage';
import AuditLogPage from '@/pages/fo/AuditLogPage';
import ReportsPage from '@/pages/fo/ReportsPage';
import AnalyticsDashboardPage from '@/pages/fo/AnalyticsDashboardPage';
import AIChatPage from '@/pages/fo/AIChatPage';
import SmartInsightsPage from '@/pages/fo/SmartInsightsPage';
import AlertsPage from '@/pages/fo/AlertsPage';
import ConsolidatedDashboard from '@/pages/fo/ConsolidatedDashboard';
import RealEstateManagementPage from '@/pages/fo/RealEstateManagementPage';
import OthersPage from '@/pages/fo/OthersPage';
import MarketOutlookPage from '@/pages/fo/MarketOutlookPage';
import PrivateMarketsDashboard from '@/pages/fo/PrivateMarketsDashboard';
import SharePointCallback from '@/pages/fo/SharePointCallback';

// Base protected route — redirects to / if not authenticated
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" />;
  return children;
};

// Permission-gated route — redirects to /consolidated with a toast if permission missing
const PermissionRoute = ({ permission, children }) => {
  const { hasPermission } = useAuth();
  if (!hasPermission(permission)) {
    toast.error("You don't have permission to access that page.");
    return <Navigate to="/consolidated" replace />;
  }
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#ffd87a] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#a8a8a8]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={user ? <Navigate to="/consolidated" /> : <HomePage />} />
      <Route path="/login" element={user ? <Navigate to="/consolidated" /> : <LoginPage />} />

      {/* OAuth callback — needs to be outside FOLayout */}
      <Route path="/sharepoint-callback" element={<SharePointCallback />} />

      {/* Protected Routes — nested under shared FOLayout */}
      <Route element={<ProtectedRoute><FOLayout /></ProtectedRoute>}>
        {/* All authenticated users */}
        <Route path="/consolidated" element={<ConsolidatedDashboard />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Analytics — requires analytics:read */}
        <Route path="/analytics" element={
          <PermissionRoute permission="analytics:read"><AnalyticsDashboardPage /></PermissionRoute>
        } />

        {/* Portfolio / Markets — requires portfolios:read */}
        <Route path="/markets" element={
          <PermissionRoute permission="portfolios:read"><PublicMarketsDashboard /></PermissionRoute>
        } />
        <Route path="/portfolio" element={
          <PermissionRoute permission="portfolios:read"><PortfolioManagementPage /></PermissionRoute>
        } />
        <Route path="/portfolio/:assetClass" element={
          <PermissionRoute permission="portfolios:read"><HoldingsPage /></PermissionRoute>
        } />
        <Route path="/transactions" element={
          <PermissionRoute permission="portfolios:read"><TransactionsPage /></PermissionRoute>
        } />

        {/* Tax — requires portfolios:read (all roles that can view portfolios can view tax) */}
        <Route path="/tax" element={
          <PermissionRoute permission="portfolios:read"><CapitalGainsPage /></PermissionRoute>
        } />

        {/* Import — requires data:import */}
        <Route path="/import" element={
          <PermissionRoute permission="data:import"><DataImportPage /></PermissionRoute>
        } />

        {/* AI */}
        <Route path="/ai-chat" element={
          <PermissionRoute permission="ai:access"><AIChatPage /></PermissionRoute>
        } />
        <Route path="/insights" element={
          <PermissionRoute permission="ai:access"><SmartInsightsPage /></PermissionRoute>
        } />

        {/* Alerts */}
        <Route path="/alerts" element={
          <PermissionRoute permission="alerts:read"><AlertsPage /></PermissionRoute>
        } />

        {/* Others (Real Estate, Gold, Private Credit, Alternatives) */}
        <Route path="/others" element={
          <PermissionRoute permission="real_estate:read"><OthersPage /></PermissionRoute>
        } />
        <Route path="/real-estate" element={
          <PermissionRoute permission="real_estate:read"><RealEstateManagementPage /></PermissionRoute>
        } />

        {/* Private Markets dashboard (VC, PE, Credit, Infra) */}
        <Route path="/private-markets" element={
          <PermissionRoute permission="deals:read"><PrivateMarketsDashboard /></PermissionRoute>
        } />

        {/* Deal Pipeline */}
        <Route path="/deals" element={
          <PermissionRoute permission="deals:read"><DealsPage /></PermissionRoute>
        } />
        <Route path="/deals/:dealId" element={
          <PermissionRoute permission="deals:read"><DealDetailPage /></PermissionRoute>
        } />

        {/* Approvals */}
        <Route path="/approvals" element={
          <PermissionRoute permission="approvals:read"><ApprovalsPage /></PermissionRoute>
        } />

        {/* Audit Log */}
        <Route path="/audit-log" element={
          <PermissionRoute permission="audit:read"><AuditLogPage /></PermissionRoute>
        } />

        {/* Reports */}
        <Route path="/reports" element={
          <PermissionRoute permission="reports:read"><ReportsPage /></PermissionRoute>
        } />

        {/* Market Outlook */}
        <Route path="/market-outlook" element={
          <PermissionRoute permission="market_outlook:read"><MarketOutlookPage /></PermissionRoute>
        } />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to={user ? "/consolidated" : "/"} />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <RegionProvider>
          <AppDataProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </AppDataProvider>
        </RegionProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
